import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Textarea, NumInput } from "@/components/UI";
import { Plus, FlaskConical, Pencil, Trash2, Copy, FileText, CheckCircle, XCircle, History, Package, ShieldCheck, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { BulkInvWizard } from "../components/BulkInvWizard";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import { openPdfPreview } from "@/lib/pdfPreview";
import { useDragReorder } from "@/lib/useDragReorder";
import { RegulatoryCheckModal } from "@/components/RegulatoryCheckModal";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import ProductCardModal from "@/components/ProductCardModal";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { diffRevisionLines, diffKindClass } from "@/lib/revisionDiff";

// ---------------- Material Autocomplete ----------------
function MatAutocomplete({ value, materials, filterCat, onPick, testid }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const suggestions = useMemo(() => {
    if (!q) return [];
    const t = q.toLowerCase();
    const list = materials.filter(m => !filterCat || filterCat(m));
    const starts = [], contains = [];
    for (const m of list) {
      const n = (m.name || "").toLowerCase();
      if (n.startsWith(t)) starts.push(m);
      else if (n.includes(t)) contains.push(m);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [q, materials, filterCat]);
  return (
    <div ref={wrapRef} className="relative">
      <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); onPick({ name: e.target.value, is_manual: !materials.find(m => (m.name||"").toLowerCase() === e.target.value.toLowerCase()) }); }} onFocus={() => setOpen(true)}
        placeholder="Malzeme adı yazın…" className="w-full px-2 py-1 text-xs border rounded" data-testid={testid}/>
      {open && q && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-56 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg text-xs">
          {suggestions.length > 0 ? suggestions.map(m => (
            <button key={m.id} type="button" onMouseDown={e => { e.preventDefault(); onPick({ material_id: m.id, name: m.name, unit: m.unit, unit_cost: m.unit_cost, is_manual: false }); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 hover:bg-indigo-50 flex justify-between gap-2 border-b border-slate-100 last:border-0">
              <span className="truncate">{m.name}</span><span className="text-[10px] text-slate-500 shrink-0">{m.unit}</span>
            </button>
          )) : <div className="px-2 py-1.5 text-slate-500 italic">Envanterde yok — manuel olarak kullanılacak.</div>}
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE = {
  draft:    { label: "Onay Bekliyor",   c: "bg-yellow-100 text-yellow-800 ring-yellow-600/20" },
  approved: { label: "Onaylandı",       c: "bg-emerald-100 text-emerald-700 ring-emerald-600/20" },
  rejected: { label: "Onaylanmadı",     c: "bg-red-100 text-red-700 ring-red-600/20" },
  obsolete: { label: "Eski",            c: "bg-slate-100 text-slate-600 ring-slate-500/20" },
};
const StatusBadge = ({ s }) => {
  const b = STATUS_BADGE[s] || STATUS_BADGE.draft;
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${b.c}`}>{b.label}</span>;
};

// iter274 — Ürün Dayanım Süresi (shelf life). Ay bazlı seçim + gün detayı.
// Değer daima gün olarak saklanır (Product.shelf_life_days). Kullanıcı ayı değiştirince
// gün 30x otomatik hesaplanır; günü elle değiştirebilir (özel gün sayısı için).
function ShelfLifeInput({ days, onChange, testid, label }) {
  const _days = Number.isFinite(+days) ? Math.max(0, parseInt(days)) : 730;
  const months = Math.round(_days / 30);
  return (
    <div>
      <span className="text-xs uppercase text-slate-500 block mb-1">{label || "Ürün Dayanım Süresi"}</span>
      <div className="flex items-center gap-1">
        <select
          value={months}
          onChange={(e) => onChange(parseInt(e.target.value) * 30)}
          className="px-2 py-1.5 text-sm border rounded"
          data-testid={`${testid}-months`}
          title="Ay bazında seç — gün alanı otomatik güncellenir"
        >
          {[1, 3, 6, 9, 12, 18, 24, 30, 36, 48, 60].map(m => (
            <option key={m} value={m}>{m} ay</option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          value={_days}
          onChange={(e) => onChange(Math.max(1, parseInt(e.target.value) || 1))}
          className="flex-1 min-w-0 px-2 py-1.5 text-sm border rounded font-mono"
          data-testid={`${testid}-days`}
          title="Üretim tarihinden itibaren gün cinsinden dayanım"
        />
        <span className="text-[11px] text-slate-500">gün</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">Üretim tarihinden itibaren geçerli — ~{months} ay</div>
    </div>
  );
}

function newFormula() {
  const qc = [
    { name: "pH", type: "number", lower: 5.0, upper: 8.0, unit: "", notes: "" },
    { name: "Viskozite", type: "number", lower: 500, upper: 3000, unit: "cP", notes: "" },
    { name: "Görünüm", type: "text", expected_text: "Berrak", notes: "" },
    { name: "Koku", type: "text", expected_text: "Karakteristik", notes: "" },
  ];
  // useNewProduct + new_product: elle formül oluştururken kullanıcı yeni ürün oluşturabilsin
  // veya mevcut müşteri+marka ürünlerinden birini seçebilsin.
  return { customer_id: "", brand_id: "", product_id: "", product_name: "", unit_ml: 100, batch_size_kg: 100,
           useNewProduct: true, new_product: { name: "", fill_qty: 100, fill_unit: "ml", units_per_carton: 24, shelf_life_days: 730 },
           lines: [], packaging_lines: [], qc_specs: qc, extra_costs: [{ name: "İşçilik", amount: 0, notes: "" }], sales_price_eur: 0, notes: "" };
}

// "İşçilik" satırı sabit bir Ek Maliyet parametresidir (yeni formül + düzenlemede).
// Değer manuel girilir; kullanıcı silse bile edit'de otomatik geri gelir.
function ensureIscilik(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  if (!arr.some(x => ((x?.name || "").trim().toLocaleLowerCase("tr") === "işçilik"))) {
    arr.unshift({ name: "İşçilik", amount: 0, notes: "" });
  }
  return arr;
}

export function FormulasPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "system_admin";
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(newFormula());
  const [filteredRows, setFilteredRows] = useState([]);
  // iter187 FAZ 3 / iter188 — Mevzuata Göre Kontrol Et (shared modal)
  const [complianceFor, setComplianceFor] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [revsOpen, setRevsOpen] = useState(null);
  const [revs, setRevs] = useState([]);
  const visibleRows = filteredRows.length > 0 || rows.length === 0 ? filteredRows : rows;
  const pg = usePagination(visibleRows, "formulas", 100);
  // "Envantere Kaydet" (formül line'ından manuel malzemeyi envanter'e ekleme) — Fix Pkg 05
  const [invDraft, setInvDraft] = useState(null);   // {line_index, line_kind:'raw'|'pkg', name, unit, unit_cost, category}
  const [invBusy, setInvBusy] = useState(false);
  // Iter 91: Toplu Envanter Kaydet Wizard
  const [bulkInv, setBulkInv] = useState(null);  // {rows:[{kind,index,name,category,unit,unit_cost,status}]}
  const [bulkBusy, setBulkBusy] = useState(false);
  // iter296 — Proje No tıklaması artık PDF önizleme yerine formül modalını
  // salt-okunur modda açar (kullanıcı isteği: "pdf önizleme yerine düzenle
  // sayfası gibi formül + fiyatlar görünsün"). `viewOnly` true iken tüm
  // input'lar disabled, footer "Kapat" + "Düzenle" olarak değişir.
  const [viewOnly, setViewOnly] = useState(false);
  // iter279 — Ürün Kart özeti modal
  const [productCardId, setProductCardId] = useState(null);
  const openProjectPreview = (r) => {
    // iter296 — Formül önizlemesi: edit modalını salt-okunur aç.
    openEdit(r);
    setViewOnly(true);
  };

  const load = useCallback(() => Promise.all([api.get("/formulas"), api.get("/products"), api.get("/customers"), api.get("/brands"), api.get("/materials"), api.get("/quotations")])
    .then(([fo, p, c, b, m, q]) => { setRows(fo); setProducts(p); setCustomers(c); setBrands(b); setMaterials(m); setQuotations(q); }), []);
  useEffect(() => { load(); }, [load]);

  // ---- Live cost per row using CURRENT material prices (auto-updates when material.unit_cost changes)
  // Backend stores r.cost as a snapshot at creation/update time. For the list view we recompute
  // cost_per_kg + packaging_cost_per_unit from live materials so güncel fiyatla maliyet gösterilir.
  const liveCostForRow = useCallback((r) => {
    const rawLines = r.raw_lines || r.lines || [];
    const pkgLines = r.packaging_lines || [];
    const cost_per_kg = rawLines.reduce((s, l) => {
      const m = materials.find(x => x.id === l.material_id);
      const unit = m ? (parseFloat(m.unit_cost) || 0) : (parseFloat(l.manual_cost) || 0);
      return s + ((parseFloat(l.percentage) || 0) / 100) * unit;
    }, 0);
    const packaging_cost_per_unit = pkgLines.reduce((s, pl) => {
      const m = materials.find(x => x.id === pl.material_id);
      const unit = m ? (parseFloat(m.unit_cost) || 0) : (parseFloat(pl.manual_cost) || 0);
      return s + (parseFloat(pl.qty_per_unit) || 0) * unit;
    }, 0);
    return { cost_per_kg, packaging_cost_per_unit };
  }, [materials]);

  // ---- Quotation match: find latest quotation matching project_no + customer_id
  const matchedQuote = (r) => {
    if (r.source_quotation_id) return quotations.find(q => q.id === r.source_quotation_id);
    if (!r.project_no || !r.customer_id) return null;
    return quotations.find(q => q.project_no === r.project_no && q.customer_id === r.customer_id) || null;
  };
  const effectiveSales = (r) => {
    if (r.sales_price_eur) return r.sales_price_eur;
    const q = matchedQuote(r);
    const line = q?.lines?.find(l => l.unit_price);
    return line?.unit_price || 0;
  };

  // ---- Save
  const save = async () => {
    if (!f.customer_id) { alert("Müşteri seçin"); return; }
    if (f.useNewProduct && !f.brand_id) { alert("Yeni ürün için Marka seçimi zorunludur"); return; }
    // Fix Pkg 05: Yeni Ürün Oluştur ↔ Mevcut Ürün Seç — elle formüller için ürün akışı.
    if (f.useNewProduct) {
      if (!f.new_product?.name?.trim()) { alert("Yeni ürün adı zorunlu"); return; }
    } else {
      if (!f.product_id) { alert("Mevcut ürün seçin veya 'Yeni Ürün Oluştur' seçeneğini işaretleyin"); return; }
    }
    try {
      let payload = { ...f };
      if (f.useNewProduct) {
        // iter298 — Yeni ürün oluşturma: hem yeni formül hem de düzenleme
        // (özellikle "Kopyala" sonrası) için çalışır. Eskiden `!editing` guard'ı
        // yüzünden kopyalanan formülün ürün adı değiştirilip kaydedildiğinde
        // ürün oluşturulmuyor, formül eski product_id/product_name ile
        // güncelleniyordu → listede ürün adı değişmiyordu. Guard kaldırıldı.
        const p = await api.post("/products", {
          customer_id: f.customer_id,
          brand_id: f.brand_id,
          name: f.new_product.name.trim(),
          fill_qty: parseFloat(f.new_product.fill_qty) || 0,
          fill_unit: f.new_product.fill_unit || "ml",
          units_per_carton: parseInt(f.new_product.units_per_carton) || 24,
          shelf_life_days: parseInt(f.new_product.shelf_life_days) || 730,
        });
        payload.product_id = p.id;
        payload.product_name = p.name;
        payload.unit_ml = payload.unit_ml || p.fill_qty || 100;
      } else {
        const existing = products.find(p => p.id === f.product_id);
        payload.product_id = f.product_id;
        payload.product_name = existing?.name || f.product_name;
        payload.unit_ml = payload.unit_ml || existing?.fill_qty || 100;
        // iter274 — Kullanıcı mevcut ürünün dayanım süresini değiştirdiyse ürün kaydını güncelle
        const newShelf = parseInt(f.new_product?.shelf_life_days) || null;
        if (existing && newShelf && newShelf !== (existing.shelf_life_days || null)) {
          try {
            await api.put(`/products/${existing.id}`, {
              customer_id: existing.customer_id, brand_id: existing.brand_id,
              name: existing.name, fill_qty: existing.fill_qty, fill_unit: existing.fill_unit,
              units_per_carton: existing.units_per_carton, shelf_life_days: newShelf,
            });
          } catch (e) { console.warn("shelf_life update failed", e); }
        }
      }
      // useNewProduct + new_product alanları formula doc'una yazılmasın
      delete payload.useNewProduct;
      delete payload.new_product;
      if (editing) await api.put(`/formulas/${editing.id}`, payload);
      else await api.post("/formulas", payload);
      setOpen(false); setEditing(null); setF(newFormula()); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const openNew = () => { setEditing(null); setF(newFormula()); setViewOnly(false); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setViewOnly(false);
    // iter274 — Var olan ürünün shelf_life_days'ini forma yükle ki kullanıcı görsün + düzenleyebilsin
    const existingProduct = r.product_id ? products.find(p => p.id === r.product_id) : null;
    const existingShelfDays = existingProduct?.shelf_life_days ?? 730;
    setF({
      ...newFormula(),
      ...r,
      product_name: r.product_name || r.name || "",
      // Düzenleme: mevcut ürün var → useNewProduct=false ve product_id set.
      useNewProduct: !r.product_id,
      new_product: { name: r.product_name || r.name || "", fill_qty: r.unit_ml || 100, fill_unit: "ml", units_per_carton: existingProduct?.units_per_carton || 24, shelf_life_days: existingShelfDays },
      lines: (r.lines || []).map(l => ({ ...l, notes: l.notes || l.instructions || "", material_name: l.material_name || (materials.find(m => m.id === l.material_id)?.name || "") })),
      extra_costs: ensureIscilik(r.extra_costs),
    });
    setOpen(true);
  };
  // Fix Pkg 05: Formül line'ından "Envantere Kaydet" (SO detay'ındakine paralel akış).
  const saveInvFromFormula = async () => {
    if (invBusy) return;
    if (!invDraft?.name?.trim()) return alert("Malzeme adı zorunlu");
    if (!invDraft?.category) return alert("Kategori zorunlu");
    setInvBusy(true);
    try {
      let matId = null;
      try {
        const mat = await api.post("/materials", {
          name: invDraft.name.trim(), category: invDraft.category, unit: invDraft.unit || "kg",
          unit_cost: parseFloat(invDraft.unit_cost || 0), primary_supplier: "", currency: "EUR",
          min_stock: 0, critical_stock: 0, lead_time_days: 0,
        });
        matId = mat.id;
      } catch (e) {
        const err = e.response?.data;
        if (err?.detail?.code === "duplicate_material_name" && err.detail.existing_id) matId = err.detail.existing_id;
        else throw e;
      }
      await api.post("/formulas/link-manual-material", { material_id: matId, material_name: invDraft.name.trim() });
      // Optimistic update: mevcut modal state'te bu satırı bağla
      setF(prev => {
        const upd = { ...prev };
        if (invDraft.line_kind === "raw") {
          upd.lines = (prev.lines || []).map((l, i) => i === invDraft.line_index ? { ...l, material_id: matId, is_manual: false } : l);
        } else {
          upd.packaging_lines = (prev.packaging_lines || []).map((l, i) => i === invDraft.line_index ? { ...l, material_id: matId, is_manual: false } : l);
        }
        return upd;
      });
      // Materials listesini de tazele (autocomplete için)
      try { const mats = await api.get("/materials"); setMaterials(mats); } catch (_) {}
      setInvDraft(null);
      // Ana listede status yansısın diye backend'in ihtiyacı olan SO reservation reconcile
      // link endpoint tarafında zaten yapıldı — sayfa refresh gerekmez.
      load();
    } catch (e) {
      const err = e.response?.data;
      alert(err?.detail?.message || err?.detail || "Envanter kaydı oluşturulamadı");
    } finally {
      setInvBusy(false);
    }
  };

  // Iter 91: Toplu envanter kaydet — tüm is_manual line'ları tek modalda topla
  const openBulkInv = () => {
    const rows = [];
    (f.lines || []).forEach((l, i) => {
      if (l.is_manual) rows.push({ kind: "raw", index: i, name: l.material_name || "", unit: l.unit || "kg", unit_cost: l.manual_cost || 0, category: "raw_material", status: "pending" });
    });
    (f.packaging_lines || []).forEach((pl, i) => {
      if (pl.is_manual) rows.push({ kind: "pkg", index: i, name: pl.material_name || "", unit: pl.unit || "adet", unit_cost: pl.manual_cost || 0, category: "other_packaging", status: "pending" });
    });
    if (rows.length === 0) { alert("Envantere kaydedilecek manuel malzeme bulunamadı"); return; }
    setBulkInv({ rows });
  };

  const saveBulkInv = async () => {
    if (bulkBusy || !bulkInv) return;
    // Validate: all names + categories present
    for (const r of bulkInv.rows) {
      if (!r.name?.trim()) { alert(`Boş isim: satır ${r.kind}#${r.index + 1}`); return; }
      if (!r.category) { alert(`Kategori seçilmedi: ${r.name}`); return; }
    }
    setBulkBusy(true);
    const results = [];
    for (let i = 0; i < bulkInv.rows.length; i++) {
      const r = bulkInv.rows[i];
      try {
        let matId = null;
        try {
          const mat = await api.post("/materials", {
            name: r.name.trim(), category: r.category, unit: r.unit || "kg",
            unit_cost: parseFloat(r.unit_cost || 0), primary_supplier: "", currency: "EUR",
            min_stock: 0, critical_stock: 0, lead_time_days: 0,
          });
          matId = mat.id;
        } catch (e) {
          const err = e.response?.data;
          if (err?.detail?.code === "duplicate_material_name" && err.detail.existing_id) matId = err.detail.existing_id;
          else throw e;
        }
        const link = await api.post("/formulas/link-manual-material", { material_id: matId, material_name: r.name.trim() });
        const linked = !!link?.formulas_updated;
        results.push({ ...r, mat_id: matId, linked, status: linked ? "success" : "warn_no_match" });
        // Update this row in bulkInv state so user sees progress
        setBulkInv(prev => prev ? { rows: prev.rows.map((x, j) => j === i ? { ...x, status: linked ? "success" : "warn_no_match", mat_id: matId } : x) } : prev);
      } catch (e) {
        const err = e.response?.data;
        results.push({ ...r, error: err?.detail?.message || err?.detail || "Hata", status: "error" });
        setBulkInv(prev => prev ? { rows: prev.rows.map((x, j) => j === i ? { ...x, status: "error", error: err?.detail?.message || err?.detail || "Hata" } : x) } : prev);
      }
    }
    // Optimistic update: successful rows'u f.lines / f.packaging_lines'a yansıt
    setF(prev => {
      const upd = { ...prev };
      const rawUpd = [...(prev.lines || [])];
      const pkgUpd = [...(prev.packaging_lines || [])];
      results.forEach(r => {
        if (r.status !== "success" || !r.mat_id) return;
        if (r.kind === "raw") rawUpd[r.index] = { ...rawUpd[r.index], material_id: r.mat_id, is_manual: false };
        else pkgUpd[r.index] = { ...pkgUpd[r.index], material_id: r.mat_id, is_manual: false };
      });
      upd.lines = rawUpd; upd.packaging_lines = pkgUpd;
      return upd;
    });
    try { const mats = await api.get("/materials"); setMaterials(mats); } catch (_) {}
    setBulkBusy(false);
    // Show summary; keep modal open if there are warnings/errors for user review
    const ok = results.filter(r => r.status === "success").length;
    const warn = results.filter(r => r.status === "warn_no_match").length;
    const err = results.filter(r => r.status === "error").length;
    if (err === 0 && warn === 0) {
      setBulkInv(null);
      alert(`✅ ${ok} manuel malzemenin tamamı envantere kaydedildi ve formüle bağlandı.`);
    }
    load();
  };

  const approve = async (id) => { try { await api.post(`/formulas/${id}/approve`); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); } };
  const doReject = async () => { try { await api.post(`/formulas/${rejectOpen.id}/reject`, { reason: rejectReason }); setRejectOpen(null); setRejectReason(""); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); } };
  const copy = async (id) => {
    // iter298 — Kopyala sonrası formülü otomatik olarak edit modalında aç,
    // "Yeni Ürün Oluştur" radio'sunu preselect et ve mevcut ürün adını
    // "(Kopya)" ekiyle new_product.name'e doldur. Kullanıcı adı değiştirip
    // Kaydet'e bastığında yeni ürün oluşturulur ve formül ona bağlanır.
    try {
      const copied = await api.post(`/formulas/${id}/copy`);
      const srcProduct = copied.product_id ? products.find(p => p.id === copied.product_id) : null;
      const baseName = srcProduct?.name || copied.product_name || copied.name || "";
      const proposedName = baseName ? `${baseName} (Kopya)` : "";
      // Reload liste, sonra edit modalı aç
      await load();
      setEditing(copied);
      setViewOnly(false);
      setF({
        ...newFormula(),
        ...copied,
        product_name: baseName,
        useNewProduct: true, // yeni ürün olarak kaydetme akışını preselect
        new_product: {
          name: proposedName,
          fill_qty: copied.unit_ml || srcProduct?.fill_qty || 100,
          fill_unit: srcProduct?.fill_unit || "ml",
          units_per_carton: srcProduct?.units_per_carton || 24,
          shelf_life_days: srcProduct?.shelf_life_days ?? 730,
        },
        lines: (copied.lines || []).map(l => ({ ...l, notes: l.notes || l.instructions || "", material_name: l.material_name || (materials.find(m => m.id === l.material_id)?.name || "") })),
        extra_costs: ensureIscilik(copied.extra_costs),
      });
      setOpen(true);
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const del = async (r) => { if (!window.confirm(`${r.project_no || r.name} arşivlensin mi?`)) return; try { await api.del(`/formulas/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); } };
  const downloadPdf = async (r) => { try { const blob = await api.getBlob(`/formulas/${r.id}/pdf`); openPdfPreview(blob, `Uretim-Emri_${r.project_no || r.code}.pdf`); } catch { alert("PDF alınamadı"); } };
  const showRevs = async (r) => { try { const list = await api.get(`/formulas/${r.id}/revisions`); setRevs(list); setRevsOpen(r); } catch { alert("Revizyonlar yüklenemedi"); } };

  // ---- On customer change → auto-populate address & filter brands
  const onCustomerChange = (id) => setF(prev => ({ ...prev, customer_id: id, brand_id: "" }));
  // iter269 — Inline müşteri oluşturma modalı state (Formuller Yeni/Düzenle)
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ legal_name: "", code: "", city: "", tax_no: "" });
  const [newCustBusy, setNewCustBusy] = useState(false);
  const [custSearch, setCustSearch] = useState("");
  useEffect(() => {
    // formda kayıtlı müşteri değişince arama metnini senkron tut
    const c = customers.find(x => x.id === f.customer_id);
    setCustSearch(c?.legal_name || "");
  }, [f.customer_id, customers]);
  const createCustomerInline = async () => {
    const nm = (newCust.legal_name || "").trim();
    if (!nm) { alert("Müşteri adı gerekli"); return; }
    setNewCustBusy(true);
    try {
      const created = await api.post("/customers", { ...newCust, legal_name: nm, active: true });
      // Master listeyi güncellemek için load() gerekli; ancak local state ile hemen bağla
      // Not: rows load'ta customers zaten refresh oluyor.
      onCustomerChange(created.id);
      setCustSearch(created.legal_name || nm);
      // If customers list not yet has it, add locally so <Select> Marka çalışsın
      if (!customers.some(c => c.id === created.id)) {
        customers.push(created);
      }
      setNewCustOpen(false);
      setNewCust({ legal_name: "", code: "", city: "", tax_no: "" });
    } catch (e) { alert(e.response?.data?.detail || "Müşteri oluşturulamadı"); }
    finally { setNewCustBusy(false); }
  };
  const filteredBrands = brands.filter(b => !f.customer_id || b.customer_id === f.customer_id);

  // ---- Live cost (approximation, backend recomputes)
  // iter297 — Hammadde + Ambalaj satırları drag-and-drop yeniden sıralama.
  const linesDnd = useDragReorder(() => f.lines || [], (arr) => setF(prev => ({ ...prev, lines: arr })));
  const pkgDnd = useDragReorder(() => f.packaging_lines || [], (arr) => setF(prev => ({ ...prev, packaging_lines: arr })));
  const total = (f.lines || []).reduce((s, l) => s + (parseFloat(l.percentage) || 0), 0);
  const costPerKg = (f.lines || []).reduce((s, l) => {
    const m = materials.find(x => x.id === l.material_id);
    const c = m ? (m.unit_cost || 0) : (parseFloat(l.manual_cost) || 0);
    return s + (parseFloat(l.percentage) || 0) / 100 * c;
  }, 0);
  const massKg = (parseFloat(f.unit_ml) || 0) / 1000;
  const pkgCost = (f.packaging_lines || []).reduce((s, pl) => {
    const m = materials.find(x => x.id === pl.material_id);
    return s + (parseFloat(pl.qty_per_unit) || 0) * (m?.unit_cost || parseFloat(pl.manual_cost) || 0);
  }, 0);
  const extraCost = (f.extra_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const unitCost = costPerKg * massKg + pkgCost + extraCost;

  // Quotation match preview in form
  const matchedQuoteForm = useMemo(() => {
    if (!f.customer_id) return null;
    // Yeni formül: editing yok — quotation eşleştirme henüz mümkün değil (project_no yok).
    // Düzenleme: editing.project_no + customer_id ile eşleştir.
    return quotations.find(q => q.project_no === (editing?.project_no) && q.customer_id === f.customer_id) || null;
  }, [quotations, f.customer_id, editing]);

  // Fiyat teklifinden birim satış fiyatını çek (varsa) — auto-populate.
  const quotedUnitPrice = useMemo(() => {
    const q = matchedQuoteForm;
    if (!q) return null;
    const line = (q.lines || []).find(l => Number(l?.unit_price) > 0);
    return line ? Number(line.unit_price) : null;
  }, [matchedQuoteForm]);

  // Modal ilk açıldığında sales_price_eur boş/0 ise fiyat teklifinden otomatik doldur.
  // Kullanıcı elle düzenlerse dokunmayız — override edilirse "manuel override" etiketi görünür.
  const autofilledRef = React.useRef(null);
  useEffect(() => {
    if (!open) { autofilledRef.current = null; return; }
    const key = `${editing?.id || "new"}-${matchedQuoteForm?.id || "none"}`;
    if (autofilledRef.current === key) return; // aynı context'te yeniden doldurma
    if (quotedUnitPrice != null && (!f.sales_price_eur || f.sales_price_eur === 0)) {
      setF(prev => ({ ...prev, sales_price_eur: quotedUnitPrice }));
      autofilledRef.current = key;
    }
  }, [open, editing, matchedQuoteForm, quotedUnitPrice, f.sales_price_eur]);

  return (
    <div>
      <PageHeader title="Formüller" description="Üretim formülleri — proje bazlı revizyon takibi" actions={<Btn onClick={openNew} data-testid="new-formula"><Plus size={14}/> Yeni Formül</Btn>} />
      <FilterAndReportBar
        reportType="formulas" rows={rows} customers={customers}
        statuses={[{value:"draft",label:"Onay Bekliyor"},{value:"approved",label:"Onaylandı"},{value:"rejected",label:"Onaylanmadı"}]}
        dateAccessor={r => r.created_at}
        customerAccessor={r => r.customer_id}
        productAccessor={r => r.product_name || r.name || ""}
        statusAccessor={r => r.status}
        onFiltered={setFilteredRows}
      />
      <Table columns={[
        { label: "Proje No", render: r => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openProjectPreview(r); }}
              className="font-mono text-xs text-indigo-700 hover:text-indigo-900 hover:underline decoration-dotted"
              title="Formül önizlemesini aç"
              data-testid={`fm-proj-${r.id}`}
            >{r.project_no || "—"}</button>
            <button className="text-[10px] text-slate-500 hover:text-slate-700" onClick={() => showRevs(r)} data-testid={`fm-revs-${r.id}`} title="Revizyon geçmişi"><History size={10} className="inline"/> {r.revision || "R00"}</button>
          </div>
        )},
        { label: "Müşteri", render: r => customers.find(c => c.id === r.customer_id)?.legal_name || "—" },
        { label: "Ürün Adı", render: r => r.product_id ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setProductCardId(r.product_id); }}
            className="text-slate-800 hover:text-indigo-700 hover:underline decoration-dotted text-left"
            title="Ürün kartını aç — formüller + üretim + teslimat özeti"
            data-testid={`fm-prod-${r.id}`}
          >{r.product_name || r.name || "—"}</button>
        ) : (r.product_name || r.name || "—") },
        // [msg-145 Bölüm 3+4] Maliyet + Kar kolonları yalnızca Sistem Yöneticisi'ne görünür.
        // Satış (müşteri-facing quotation kaynağı) tüm rollere görünür.
        ...(isAdmin ? [{ label: "Maliyet", render: r => {
          // Güncel malzeme fiyatlarıyla canlı hesap — materyaller değişince otomatik güncellenir.
          const live = liveCostForRow(r);
          const snap = r.cost || {};
          const cpk = live.cost_per_kg || snap.cost_per_kg || 0;
          const ml = r.unit_ml || products.find(p => p.id === r.product_id)?.fill_qty || 100;
          const massKg = ml / 1000;
          const pkg = live.packaging_cost_per_unit || snap.packaging_cost_per_unit || 0;
          const extra = (r.extra_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
          const uc = cpk * massKg + pkg + extra;
          const snapUc = (snap.cost_per_kg || 0) * massKg + (snap.packaging_cost_per_unit || 0) + extra;
          const drift = Math.abs(uc - snapUc) > 0.0001;
          return (
            <span className="font-mono text-xs text-indigo-700 inline-flex items-center gap-1" data-testid={`fm-cost-${r.id}`}>
              €{uc.toFixed(4)}
              {drift && <span className="text-[9px] px-1 bg-amber-100 text-amber-800 rounded" title={`Snapshot: €${snapUc.toFixed(4)} — güncel fiyatlar kullanılıyor`}>güncel</span>}
            </span>
          );
        }}] : []),
        { label: "Satış", render: r => {
          const q = matchedQuote(r);
          const sales = r.sales_price_eur || (q?.lines?.find(l => l.unit_price)?.unit_price) || 0;
          const src = r.sales_price_source || (q ? "QUOTATION" : "MANUAL");
          const ftNo = r.source_quotation_number || q?.quote_no;
          return (
            <div className="text-xs" data-testid={`fm-sales-${r.id}`}>
              <span className="font-mono font-semibold">€{Number(sales).toFixed(4)}</span>
              {ftNo && <div className="text-[10px] text-slate-500">{src === "MANUAL_OVERRIDE" ? "Manuel · " : ""}<span className="text-teal-700">{ftNo}</span></div>}
            </div>
          );
        }},
        ...(isAdmin ? [{ label: "Kar", render: r => {
          const live = liveCostForRow(r);
          const snap = r.cost || {};
          const cpk = live.cost_per_kg || snap.cost_per_kg || 0;
          const ml = r.unit_ml || products.find(p => p.id === r.product_id)?.fill_qty || 100;
          const pkg = live.packaging_cost_per_unit || snap.packaging_cost_per_unit || 0;
          const extra = (r.extra_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
          const uc = cpk * ml / 1000 + pkg + extra;
          const sales = effectiveSales(r);
          const profit = sales - uc;
          if (!sales) return <span className="text-slate-400">—</span>;
          return <span className={`font-mono text-xs ${profit >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`fm-profit-${r.id}`}>€{profit.toFixed(4)}</span>;
        }}] : []),
        { label: "Durum", render: r => <StatusBadge s={r.status}/> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
            {r.status === "draft" && <Btn size="sm" variant="approve" onClick={() => approve(r.id)} data-testid={`fm-approve-${r.id}`} title="Onayla"><CheckCircle size={12}/></Btn>}
            {r.status === "draft" && <Btn size="sm" variant="reject" onClick={() => { setRejectOpen(r); setRejectReason(""); }} data-testid={`fm-reject-${r.id}`} title="Reddet"><XCircle size={12}/></Btn>}
            {(isAdmin || r.status !== "approved") && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`fm-edit-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>}
            <Btn size="sm" variant="secondary" onClick={() => downloadPdf(r)} data-testid={`fm-pdf-${r.id}`} title="PDF"><FileText size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => setComplianceFor({ ...r, formula_id: r.id })} data-testid={`fm-compliance-${r.id}`} title="Mevzuata Göre Kontrol Et"><ShieldCheck size={12}/></Btn>
            {isAdmin && <Btn size="sm" variant="secondary" onClick={() => copy(r.id)} data-testid={`fm-copy-${r.id}`} title="Kopyala"><Copy size={12}/></Btn>}
            {isAdmin && <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`fm-del-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>}
          </div>
        )},
      ]} rows={pg.paged} testid="formulas-table"/>
      <ListPagination pagination={pg} testid="formulas-pagination"/>

      {/* ---- iter187/iter188 Mevzuata Göre Kontrol Et (shared modal) ---- */}
      <RegulatoryCheckModal
        open={!!complianceFor}
        target={complianceFor}
        onClose={() => setComplianceFor(null)}
      />

      {/* ---- Reject modal ---- */}
      <Modal open={!!rejectOpen} onClose={() => setRejectOpen(null)} title={`Formülü Reddet — ${rejectOpen?.project_no || ""}`} size="md">
        <Textarea label="Red Nedeni" value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="fm-reject-reason"/>
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setRejectOpen(null)}>Vazgeç</Btn><Btn variant="reject" onClick={doReject} data-testid="fm-reject-confirm">Reddet</Btn></div>
      </Modal>

      {/* ---- Revisions modal ---- */}
      <Modal open={!!revsOpen} onClose={() => setRevsOpen(null)} title={`Revizyon Geçmişi — ${revsOpen?.project_no || ""}`} size="lg">
        {revs.length === 0 && <div className="text-slate-400 text-sm">Revizyon kaydı yok</div>}
        {/* iter294d — Formül revizyonları backend'de created_at ASC sirali. Her r
             icin bir onceki eleman (revs[i-1]) daha eski. i=0 ilk revizyondur. */}
        {revs.map((r, i) => {
          const prev = i > 0 ? revs[i - 1] : null;
          const isFirst = i === 0;
          const diffs = isFirst ? [] : diffRevisionLines(prev?.lines || [], r.lines || []);
          return (
            <div key={r.id} className={`border-l-2 pl-3 py-2 mb-2 ${r.is_active_revision ? "border-emerald-500 bg-emerald-50/40" : "border-slate-300"}`} data-testid={`fm-rev-${r.revision}`}>
              <div className="text-sm font-semibold flex items-center gap-2">
                {r.revision} {r.is_active_revision && <span className="text-[10px] px-1 bg-emerald-600 text-white rounded">AKTİF</span>}
                <StatusBadge s={r.status}/>
              </div>
              <div className="text-xs text-slate-500">{(r.created_at || "").slice(0, 19)} — {r.created_by_name || r.revision_created_by_name || ""}</div>
              {isFirst ? (
                <div className="text-[11px] text-slate-400 mt-1">İlk kayıt (referans revizyon)</div>
              ) : diffs.length === 0 ? (
                <div className="text-[11px] text-slate-400 mt-1">Hammadde değişikliği yok (yalnız meta güncellendi)</div>
              ) : (
                <ul className="mt-1.5 flex flex-wrap gap-1" data-testid={`fm-rev-diff-${r.revision}`}>
                  {diffs.map((d, k) => (
                    <li key={k} className={`text-[11px] px-1.5 py-0.5 rounded border ${diffKindClass(d.kind)}`}>{d.text}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </Modal>

      {/* iter279 — Ürün Kart Modalı — Formüller listesinde ürün adına tıklayınca açılır */}
      <ProductCardModal productId={productCardId} onClose={() => setProductCardId(null)}/>

      {/* iter296 — Proje No tıklandığında bu modal edit görünümüyle aynı içerikte
          salt-okunur olarak açılır. Eski PDF preview modalı kaldırıldı; kullanıcı
          formu ve fiyatları düzenle sayfasındaki gibi kontrol edebilir. */}

      {/* ---- Create/Edit modal ---- */}
      <Modal open={open} onClose={() => { setOpen(false); setViewOnly(false); }}
             title={viewOnly
               ? `Formül Önizleme — ${editing?.project_no || ""} ${editing?.revision || ""}`
               : (editing ? `Formül Düzenle — ${editing.project_no || ""} ${editing.revision || ""}` : "Yeni Formül")}
             size="full">
        <fieldset disabled={viewOnly} className={viewOnly ? "fm-viewonly" : ""} style={viewOnly ? { opacity: 1 } : undefined} data-testid={viewOnly ? "fm-view-only" : "fm-edit"}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <Input label="Proje No" value={editing?.project_no || "(otomatik)"} disabled data-testid="fm-project-no"/>
          <Input label="Revizyon" value={editing?.revision || "R00"} disabled data-testid="fm-revision"/>
          <SmartSuggestInput
            label="Müşteri *"
            value={custSearch}
            selectedId={f.customer_id}
            items={customers}
            displayField="legal_name"
            idField="id"
            secondaryField={(c) => `${c.city || ""}${c.code ? " · Kod: " + c.code : ""}${c.tax_no ? " · V.No: " + c.tax_no : ""}`}
            searchAccessor={(c) => [c.code, c.city, c.tax_no]}
            placeholder="Müşteri adı yazın veya listeden seçin..."
            emptyAction={{ label: "＋ Yeni müşteri kaydı oluştur", onClick: (txt) => { setNewCust({ legal_name: txt || "", code: "", city: "", tax_no: "" }); setNewCustOpen(true); } }}
            onChange={({ value, selectedId, item }) => {
              setCustSearch(value);
              if (item) onCustomerChange(item.id);
              else if (!value) onCustomerChange("");
              // typed-only (no match) → keep customer_id blank until user picks or creates
              else if (!selectedId) setF(prev => ({ ...prev, customer_id: "" }));
            }}
            data-testid="fm-customer-smart"
          />
          <Select label="Marka" value={f.brand_id} onChange={e => setF({ ...f, brand_id: e.target.value })} data-testid="fm-brand">
            <option value="">—</option>
            {filteredBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
          {/* Fix Pkg 05: Yeni Ürün Oluştur ↔ Mevcut Ürün Seç radio */}
          <div className="col-span-4 flex items-center gap-4 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            <span className="text-xs uppercase text-slate-500">Ürün:</span>
            <label className="text-xs flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={!!f.useNewProduct} onChange={() => setF({ ...f, useNewProduct: true })} data-testid="fm-use-new-product"/>
              Yeni Ürün Oluştur
            </label>
            <label className="text-xs flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={!f.useNewProduct} onChange={() => setF({ ...f, useNewProduct: false })} data-testid="fm-use-existing-product"/>
              Mevcut Ürün Seç
            </label>
          </div>
          {f.useNewProduct ? (
            <>
              <Input label="Ürün Adı *" value={f.new_product?.name || ""} onChange={e => setF({ ...f, new_product: { ...f.new_product, name: e.target.value } })} data-testid="fm-new-product-name"/>
              <Input label="Dolum" type="number" value={f.new_product?.fill_qty || 0} onChange={e => setF({ ...f, new_product: { ...f.new_product, fill_qty: parseFloat(e.target.value) || 0 }, unit_ml: parseFloat(e.target.value) || 0 })} data-testid="fm-new-product-fillqty"/>
              <Select label="Birim" value={f.new_product?.fill_unit || "ml"} onChange={e => setF({ ...f, new_product: { ...f.new_product, fill_unit: e.target.value } })} data-testid="fm-new-product-unit">
                <option value="ml">ml</option><option value="g">g</option><option value="l">l</option><option value="kg">kg</option>
              </Select>
              <Input label="Karton/Adet" type="number" value={f.new_product?.units_per_carton || 24} onChange={e => setF({ ...f, new_product: { ...f.new_product, units_per_carton: parseInt(e.target.value) || 0 } })} data-testid="fm-new-product-carton"/>
              {/* iter274 — Ürün Dayanım Süresi (raf ömrü) editable — ay + gün ikili */}
              <ShelfLifeInput
                days={f.new_product?.shelf_life_days ?? 730}
                onChange={(days) => setF({ ...f, new_product: { ...f.new_product, shelf_life_days: days } })}
                testid="fm-new-product-shelflife"
              />
            </>
          ) : (
            <>
              <Select label="Ürün *" value={f.product_id} onChange={e => {
                const p = products.find(x => x.id === e.target.value);
                // iter274 — Mevcut ürün seçilince o ürünün shelf_life_days'ini forma yükle
                setF({ ...f, product_id: e.target.value, product_name: p?.name || "", unit_ml: p?.fill_qty || f.unit_ml,
                       new_product: { ...(f.new_product || {}), shelf_life_days: p?.shelf_life_days ?? 730 } });
              }} data-testid="fm-existing-product">
                <option value="">— Seçiniz —</option>
                {products.filter(p => !f.customer_id || p.customer_id === f.customer_id)
                        .filter(p => !f.brand_id || p.brand_id === f.brand_id)
                        .map(p => <option key={p.id} value={p.id}>{p.name}{p.fill_qty ? ` (${p.fill_qty}${p.fill_unit || "ml"})` : ""}</option>)}
              </Select>
              <Input label="ml" type="number" value={f.unit_ml} onChange={e => setF({ ...f, unit_ml: parseFloat(e.target.value) || 0 })} data-testid="fm-unit-ml"/>
              {/* iter274 — Mevcut ürün için de dayanım süresi görünür + düzenlenebilir */}
              <ShelfLifeInput
                days={f.new_product?.shelf_life_days ?? 730}
                onChange={(days) => setF({ ...f, new_product: { ...(f.new_product || {}), shelf_life_days: days } })}
                testid="fm-existing-product-shelflife"
                label="Dayanım Süresi"
              />
            </>
          )}
          <div>
            <span className="text-xs uppercase text-slate-500 block mb-1">Satış Fiyatı (EUR)</span>
            <div className="flex items-center gap-1">
              <NumInput step="0.01" value={f.sales_price_eur || 0} onChange={e => setF({ ...f, sales_price_eur: parseFloat(e.target.value) || 0 })} className="flex-1 px-2 py-1.5 text-sm border rounded" data-testid="fm-sales-price"/>
              {quotedUnitPrice != null && (
                <button
                  type="button"
                  onClick={() => setF(prev => ({ ...prev, sales_price_eur: quotedUnitPrice }))}
                  className="px-2 py-1.5 text-[10px] font-semibold rounded border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100 whitespace-nowrap"
                  title={`Fiyat teklifinden al: € ${quotedUnitPrice.toFixed(4)}`}
                  data-testid="fm-sales-price-from-quote"
                >
                  Teklifden Al
                </button>
              )}
            </div>
            {matchedQuoteForm && (
              <div className="text-[10px] text-teal-700 mt-0.5">
                Kaynak: {matchedQuoteForm.quote_no}
                {quotedUnitPrice != null && ` (€ ${quotedUnitPrice.toFixed(4)})`}
                {quotedUnitPrice != null && Number(f.sales_price_eur || 0) !== Number(quotedUnitPrice) && (
                  <span className="ml-1 text-amber-700 font-semibold">· manuel override</span>
                )}
              </div>
            )}
            {!matchedQuoteForm && f.customer_id && editing?.project_no && (
              <div className="text-[10px] text-slate-400 italic mt-0.5">Bu proje için fiyat teklifi bulunamadı — manuel girin.</div>
            )}
          </div>
        </div>

        {/* [msg-145 Bölüm 3+4] Maliyet/kg ve Birim Ürün Maliyeti yalnızca Sistem Yöneticisi'ne görünür. */}
        <div className={`grid ${isAdmin ? "grid-cols-3" : "grid-cols-1"} gap-3 mb-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded`}>
          <div className="text-sm"><span className="text-slate-500 uppercase text-[10px] block">Toplam %</span><span className={`font-mono font-semibold ${Math.abs(total - 100) > 0.01 ? "text-amber-600" : "text-emerald-600"}`} data-testid="fm-total-pct">{total.toFixed(2)}%</span></div>
          {isAdmin && <div className="text-sm"><span className="text-slate-500 uppercase text-[10px] block">Maliyet / kg</span><span className="font-mono font-semibold text-indigo-700" data-testid="fm-cost-kg">€{costPerKg.toFixed(4)} / kg</span></div>}
          {isAdmin && <div className="text-sm"><span className="text-slate-500 uppercase text-[10px] block">Birim Ürün Maliyeti ({f.unit_ml || 0} ml)</span><span className="font-mono font-semibold text-emerald-700" data-testid="fm-unit-cost">€{unitCost.toFixed(4)}</span></div>}
        </div>

        {/* Formula lines */}
        <div className="border rounded p-3 bg-slate-50/50 mb-3">
          <div className="flex justify-between mb-2"><div className="text-sm font-semibold"><FlaskConical size={14} className="inline"/> Formül Bileşenleri</div>
            <div className="flex gap-2">
              {((f.lines || []).some(l => l.is_manual) || (f.packaging_lines || []).some(pl => pl.is_manual)) && (
                <Btn size="sm" variant="secondary" onClick={openBulkInv} data-testid="fm-bulk-inv-open" className="bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-100">
                  <Package size={12}/> Tüm Manuelleri Envantere Kaydet
                </Btn>
              )}
              <Btn size="sm" variant="secondary" onClick={() => setF({ ...f, lines: [...(f.lines || []), { material_id: null, material_name: "", is_manual: false, unit: "kg", percentage: 0, notes: "" }] })} data-testid="fm-add-line"><Plus size={12}/> Malzeme Ekle</Btn>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-2 mb-1 text-[10px] uppercase text-slate-500 px-1">
            <div className="col-span-4">Malzeme Adı</div>
            <div className="col-span-1">Birim</div>
            <div className="col-span-1">%</div>
            <div className="col-span-2">Fiyat</div>
            <div className="col-span-3">Notlar</div>
            <div className="col-span-1"></div>
          </div>
          {(f.lines || []).map((l, i) => {
            const dropAbove = linesDnd.isDropTarget(i) && linesDnd.direction() === "up";
            const dropBelow = linesDnd.isDropTarget(i) && linesDnd.direction() === "down";
            const dragging = linesDnd.isDragging(i);
            return (
            <div key={i} className={`flex items-stretch gap-1 mb-1 relative transition-all ${dragging ? "opacity-50" : ""} ${linesDnd.isDropTarget(i) ? "bg-indigo-50/60 rounded" : ""}`} {...linesDnd.rowProps(i)} data-testid={`fm-line-row-${i}`}>
              {dropAbove && <div className="absolute -top-0.5 left-6 right-0 h-0.5 border-t-2 border-dashed border-indigo-500 pointer-events-none" data-testid={`fm-line-drop-above-${i}`}/>}
              {dropBelow && <div className="absolute -bottom-0.5 left-6 right-0 h-0.5 border-b-2 border-dashed border-indigo-500 pointer-events-none" data-testid={`fm-line-drop-below-${i}`}/>}
              <button type="button" draggable {...linesDnd.handleProps(i)}
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-indigo-600 flex items-center px-1 rounded hover:bg-slate-100 shrink-0"
                title="Sırayı değiştirmek için sürükleyin"
                data-testid={`fm-line-drag-${i}`}
                aria-label="Satırı sürükle"
              ><GripVertical size={14}/></button>
              <div className={`grid grid-cols-12 gap-2 flex-1 bg-white border rounded p-1 ${l.is_manual ? "border-amber-300 bg-amber-50/40" : ""}`}>
              <div className="col-span-4">
                <MatAutocomplete value={l.material_name} materials={materials}
                  filterCat={m => ["raw_material","active","fragrance","aux"].includes(m.category)}
                  onPick={pick => { const arr = [...f.lines]; arr[i] = { ...arr[i], material_id: pick.material_id || null, material_name: pick.name, unit: pick.unit || "kg", is_manual: !!pick.is_manual, manual_cost: pick.unit_cost, manual_currency: arr[i]?.manual_currency || "EUR" }; setF({ ...f, lines: arr }); }}
                  testid={`fm-line-mat-${i}`}/>
                {l.is_manual && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-[10px] text-amber-700">Manuel (envanterde yok)</div>
                    <button
                      type="button"
                      onClick={() => setInvDraft({ line_index: i, line_kind: "raw", name: l.material_name || "", unit: l.unit || "kg", unit_cost: l.manual_cost || 0, category: "raw_material" })}
                      className="text-[10px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-2 py-0.5 rounded inline-flex items-center gap-1"
                      data-testid={`fm-line-add-inv-${i}`}
                    >
                      <Package size={10}/> Envantere Kaydet
                    </button>
                  </div>
                )}
              </div>
              <select value={l.unit || "kg"} onChange={e => { const arr = [...f.lines]; arr[i] = { ...arr[i], unit: e.target.value }; setF({ ...f, lines: arr }); }} className="col-span-1 px-1 py-1 text-xs border rounded"><option value="kg">kg</option><option value="adet">adet</option></select>
              <NumInput step="0.001" value={l.percentage || 0} onChange={e => { const arr = [...f.lines]; arr[i] = { ...arr[i], percentage: parseFloat(e.target.value) || 0 }; setF({ ...f, lines: arr }); }} placeholder="%" className="col-span-1 px-2 py-1 text-xs border rounded" data-testid={`fm-line-pct-${i}`}/>
              {/* Fiyat: manuel satırda düzenlenebilir, envanter satırında salt-okunur — güncel malzeme fiyatını gösterir */}
              {l.is_manual ? (
                <div className="col-span-2 flex gap-1">
                  <NumInput step="0.0001" value={l.manual_cost || 0} onChange={e => { const arr = [...f.lines]; arr[i] = { ...arr[i], manual_cost: parseFloat(e.target.value) || 0 }; setF({ ...f, lines: arr }); }} placeholder="Birim" className="flex-1 min-w-0 px-2 py-1 text-xs border rounded" data-testid={`fm-line-cost-${i}`}/>
                  <select value={l.manual_currency || "EUR"} onChange={e => { const arr = [...f.lines]; arr[i] = { ...arr[i], manual_currency: e.target.value }; setF({ ...f, lines: arr }); }} className="px-1 py-1 text-xs border rounded" data-testid={`fm-line-cur-${i}`}>
                    <option value="EUR">€</option><option value="TRY">₺</option><option value="USD">$</option>
                  </select>
                </div>
              ) : (
                <div className="col-span-2 text-[11px] pt-1.5 font-mono text-slate-600 truncate" data-testid={`fm-line-inv-cost-${i}`}>
                  {(() => {
                    if (!l.material_id) return <span className="text-slate-400">—</span>;
                    const m = materials.find(x => x.id === l.material_id);
                    if (!m) return <span className="text-slate-400">—</span>;
                    const sym = (m.currency || "TRY").toUpperCase() === "EUR" ? "€" : ((m.currency || "TRY").toUpperCase() === "USD" ? "$" : "₺");
                    return <span title="Envanterdeki güncel birim fiyat">{sym}{Number(m.unit_cost || 0).toFixed(4)}</span>;
                  })()}
                </div>
              )}
              <input value={l.notes || ""} onChange={e => { const arr = [...f.lines]; arr[i] = { ...arr[i], notes: e.target.value }; setF({ ...f, lines: arr }); }} placeholder="Not (ör. 40°C altında)" className="col-span-3 px-2 py-1 text-xs border rounded" data-testid={`fm-line-notes-${i}`}/>
              <div className="col-span-1 flex items-center gap-0.5 justify-end">
                <button type="button" onClick={() => linesDnd.moveUp(i)} disabled={i === 0} className="text-slate-500 hover:text-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5" title="Yukarı taşı" data-testid={`fm-line-up-${i}`}><ChevronUp size={14}/></button>
                <button type="button" onClick={() => linesDnd.moveDown(i)} disabled={i === (f.lines || []).length - 1} className="text-slate-500 hover:text-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5" title="Aşağı taşı" data-testid={`fm-line-down-${i}`}><ChevronDown size={14}/></button>
                <button onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })} className="text-red-500 text-xs px-1 hover:underline" title="Sil">Sil</button>
              </div>
              </div>
            </div>
          );})}
        </div>

        {/* Packaging */}
        <div className="border rounded p-3 bg-amber-50/30 mb-3">
          <div className="flex justify-between mb-2"><div className="text-sm font-semibold">Ambalaj Bileşenleri</div>
            <Btn size="sm" variant="secondary" onClick={() => setF({ ...f, packaging_lines: [...(f.packaging_lines || []), { material_id: null, material_name: "", is_manual: false, qty_per_unit: 1, notes: "" }] })} data-testid="fm-add-pkg"><Plus size={12}/> Ambalaj Ekle</Btn>
          </div>
          <div className="grid grid-cols-12 gap-2 mb-1 text-[10px] uppercase text-slate-500 px-1">
            <div className="col-span-4">Ambalaj Adı</div>
            <div className="col-span-2">Adet/ürün</div>
            <div className="col-span-2">Fiyat</div>
            <div className="col-span-3">Notlar</div>
            <div className="col-span-1"></div>
          </div>
          {(f.packaging_lines || []).map((pl, i) => {
            const dropAbove = pkgDnd.isDropTarget(i) && pkgDnd.direction() === "up";
            const dropBelow = pkgDnd.isDropTarget(i) && pkgDnd.direction() === "down";
            const dragging = pkgDnd.isDragging(i);
            return (
            <div key={i} className={`flex items-stretch gap-1 mb-1 relative transition-all ${dragging ? "opacity-50" : ""} ${pkgDnd.isDropTarget(i) ? "bg-amber-50/70 rounded" : ""}`} {...pkgDnd.rowProps(i)} data-testid={`fm-pkg-row-${i}`}>
              {dropAbove && <div className="absolute -top-0.5 left-6 right-0 h-0.5 border-t-2 border-dashed border-amber-500 pointer-events-none" data-testid={`fm-pkg-drop-above-${i}`}/>}
              {dropBelow && <div className="absolute -bottom-0.5 left-6 right-0 h-0.5 border-b-2 border-dashed border-amber-500 pointer-events-none" data-testid={`fm-pkg-drop-below-${i}`}/>}
              <button type="button" draggable {...pkgDnd.handleProps(i)}
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-amber-600 flex items-center px-1 rounded hover:bg-amber-50 shrink-0"
                title="Sırayı değiştirmek için sürükleyin"
                data-testid={`fm-pkg-drag-${i}`}
                aria-label="Ambalaj satırını sürükle"
              ><GripVertical size={14}/></button>
              <div className={`grid grid-cols-12 gap-2 flex-1 bg-white border rounded p-1 ${pl.is_manual ? "border-amber-300" : ""}`}>
              <div className="col-span-4">
                <MatAutocomplete value={pl.material_name} materials={materials}
                  filterCat={m => (m.unit === "pcs" || m.unit === "adet") || !["raw_material","active","fragrance","aux"].includes(m.category)}
                  onPick={pick => { const arr = [...f.packaging_lines]; arr[i] = { ...arr[i], material_id: pick.material_id || null, material_name: pick.name, unit: pick.unit || "adet", is_manual: !!pick.is_manual, manual_cost: pick.unit_cost, manual_currency: arr[i]?.manual_currency || "EUR" }; setF({ ...f, packaging_lines: arr }); }}
                  testid={`fm-pkg-mat-${i}`}/>
                {pl.is_manual && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-[10px] text-amber-700">Manuel (envanterde yok)</div>
                    <button
                      type="button"
                      onClick={() => setInvDraft({ line_index: i, line_kind: "pkg", name: pl.material_name || "", unit: pl.unit || "adet", unit_cost: pl.manual_cost || 0, category: "other_packaging" })}
                      className="text-[10px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-2 py-0.5 rounded inline-flex items-center gap-1"
                      data-testid={`fm-pkg-add-inv-${i}`}
                    >
                      <Package size={10}/> Envantere Kaydet
                    </button>
                  </div>
                )}
              </div>
              <NumInput step="0.01" value={pl.qty_per_unit || 0} onChange={e => { const arr = [...f.packaging_lines]; arr[i] = { ...arr[i], qty_per_unit: parseFloat(e.target.value) || 0 }; setF({ ...f, packaging_lines: arr }); }} placeholder="Adet/ürün" className="col-span-2 px-2 py-1 text-xs border rounded" data-testid={`fm-pkg-qty-${i}`}/>
              {/* Fiyat: manuel satırda düzenlenebilir, envanter satırında salt-okunur — güncel malzeme fiyatını gösterir */}
              {pl.is_manual ? (
                <div className="col-span-2 flex gap-1">
                  <NumInput step="0.0001" value={pl.manual_cost || 0} onChange={e => { const arr = [...f.packaging_lines]; arr[i] = { ...arr[i], manual_cost: parseFloat(e.target.value) || 0 }; setF({ ...f, packaging_lines: arr }); }} placeholder="Birim" className="flex-1 min-w-0 px-2 py-1 text-xs border rounded" data-testid={`fm-pkg-cost-${i}`}/>
                  <select value={pl.manual_currency || "EUR"} onChange={e => { const arr = [...f.packaging_lines]; arr[i] = { ...arr[i], manual_currency: e.target.value }; setF({ ...f, packaging_lines: arr }); }} className="px-1 py-1 text-xs border rounded" data-testid={`fm-pkg-cur-${i}`}>
                    <option value="EUR">€</option><option value="TRY">₺</option><option value="USD">$</option>
                  </select>
                </div>
              ) : (
                <div className="col-span-2 text-[11px] pt-1.5 font-mono text-slate-600 truncate" data-testid={`fm-pkg-inv-cost-${i}`}>
                  {(() => {
                    if (!pl.material_id) return <span className="text-slate-400">—</span>;
                    const m = materials.find(x => x.id === pl.material_id);
                    if (!m) return <span className="text-slate-400">—</span>;
                    const sym = (m.currency || "TRY").toUpperCase() === "EUR" ? "€" : ((m.currency || "TRY").toUpperCase() === "USD" ? "$" : "₺");
                    return <span title="Envanterdeki güncel birim fiyat">{sym}{Number(m.unit_cost || 0).toFixed(4)}</span>;
                  })()}
                </div>
              )}
              <input value={pl.notes || ""} onChange={e => { const arr = [...f.packaging_lines]; arr[i] = { ...arr[i], notes: e.target.value }; setF({ ...f, packaging_lines: arr }); }} placeholder="Not" className="col-span-3 px-2 py-1 text-xs border rounded" data-testid={`fm-pkg-notes-${i}`}/>
              <div className="col-span-1 flex items-center gap-0.5 justify-end">
                <button type="button" onClick={() => pkgDnd.moveUp(i)} disabled={i === 0} className="text-slate-500 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5" title="Yukarı taşı" data-testid={`fm-pkg-up-${i}`}><ChevronUp size={14}/></button>
                <button type="button" onClick={() => pkgDnd.moveDown(i)} disabled={i === (f.packaging_lines || []).length - 1} className="text-slate-500 hover:text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5" title="Aşağı taşı" data-testid={`fm-pkg-down-${i}`}><ChevronDown size={14}/></button>
                <button onClick={() => setF({ ...f, packaging_lines: f.packaging_lines.filter((_, j) => j !== i) })} className="text-red-500 text-xs px-1 hover:underline" title="Sil">Sil</button>
              </div>
              </div>
            </div>
          );})}
        </div>

        {/* Extra costs */}
        <div className="border rounded p-3 bg-rose-50/30 mb-3">
          <div className="flex justify-between mb-2"><div className="text-sm font-semibold">Ek Maliyetler (₺/adet — İşçilik dahil)</div>
            <Btn size="sm" variant="secondary" onClick={() => setF({ ...f, extra_costs: [...(f.extra_costs || []), { name: "", amount: 0, notes: "" }] })} data-testid="fm-add-extra"><Plus size={12}/> Ek Maliyet</Btn>
          </div>
          <div className="text-[11px] italic text-slate-600 mb-2 bg-white/70 border border-rose-200 rounded px-2 py-1" data-testid="fm-extra-help">
            💡 <b>Her bir adet ürün için</b> giriş yapın — üretim emri açılınca sipariş adediyle otomatik çarpılır (ambalaj bileşenleri gibi). Örn: 100 ml ürün başına <b>0.10 €</b> işçilik girerseniz, 1000 adetlik siparişte 100 € işçilik gider.
          </div>
          {(f.extra_costs || []).map((x, i) => {
            const isIscilik = (x.name || "").trim().toLocaleLowerCase("tr") === "işçilik";
            return (
            <div key={i} className={`grid grid-cols-12 gap-2 mb-1 border rounded p-1 ${isIscilik ? "bg-amber-50 border-amber-300" : "bg-white"}`}>
              <input value={x.name || ""} onChange={e => { if (isIscilik) return; const arr = [...f.extra_costs]; arr[i] = { ...arr[i], name: e.target.value }; setF({ ...f, extra_costs: arr }); }} placeholder="Kalem (Etiket, Kargo…)" readOnly={isIscilik} className={`col-span-4 px-2 py-1 text-xs border rounded ${isIscilik ? "font-semibold text-amber-900 bg-amber-100 cursor-not-allowed" : ""}`} data-testid={`fm-extra-name-${i}`} title={isIscilik ? "Sabit parametre — silinemez / değiştirilemez" : ""}/>
              <div className="col-span-2 flex items-center gap-1">
                <NumInput step="0.0001" value={x.amount || 0} onChange={e => { const arr = [...f.extra_costs]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 }; setF({ ...f, extra_costs: arr }); }} placeholder="0.00" className="flex-1 px-2 py-1 text-xs border rounded" data-testid={`fm-extra-amt-${i}`}/>
                <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap" title="Adet başına Euro">€/adet</span>
              </div>
              <input value={x.notes || ""} onChange={e => { const arr = [...f.extra_costs]; arr[i] = { ...arr[i], notes: e.target.value }; setF({ ...f, extra_costs: arr }); }} placeholder="Not" className="col-span-5 px-2 py-1 text-xs border rounded" data-testid={`fm-extra-notes-${i}`}/>
              {isIscilik ? (
                <span className="col-span-1 text-[10px] text-amber-700 self-center font-semibold text-center" title="Sabit — silinemez">Sabit</span>
              ) : (
                <button onClick={() => setF({ ...f, extra_costs: f.extra_costs.filter((_, j) => j !== i) })} className="col-span-1 text-red-500 text-xs">Sil</button>
              )}
            </div>
            );
          })}
        </div>

        {/* QC specs (existing structure + notes) */}
        <div className="border rounded p-3 bg-amber-50/40 mb-3">
          <div className="text-sm font-semibold mb-2">Kalite Değerleri</div>
          {(f.qc_specs || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-1 bg-white border rounded p-1">
              <input value={s.name || ""} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], name: e.target.value }; setF({ ...f, qc_specs: sp }); }} placeholder="Parametre" className="col-span-2 px-2 py-1 text-xs border rounded" data-testid={`fm-qc-name-${i}`}/>
              <select value={s.type || "number"} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], type: e.target.value }; setF({ ...f, qc_specs: sp }); }} className="col-span-1 px-1 py-1 text-xs border rounded"><option value="number">Sayı</option><option value="text">Metin</option></select>
              {s.type === "text" ? (
                <input value={s.expected_text || ""} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], expected_text: e.target.value }; setF({ ...f, qc_specs: sp }); }} placeholder="Beklenen" className="col-span-4 px-2 py-1 text-xs border rounded"/>
              ) : (
                <>
                  <input type="number" step="0.01" value={s.lower ?? ""} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], lower: parseFloat(e.target.value) }; setF({ ...f, qc_specs: sp }); }} placeholder="Alt" className="col-span-1 px-2 py-1 text-xs border rounded"/>
                  <input type="number" step="0.01" value={s.upper ?? ""} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], upper: parseFloat(e.target.value) }; setF({ ...f, qc_specs: sp }); }} placeholder="Üst" className="col-span-1 px-2 py-1 text-xs border rounded"/>
                  <input value={s.unit || ""} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], unit: e.target.value }; setF({ ...f, qc_specs: sp }); }} placeholder="Birim" className="col-span-2 px-2 py-1 text-xs border rounded"/>
                </>
              )}
              <input value={s.notes || ""} onChange={e => { const sp = [...(f.qc_specs || [])]; sp[i] = { ...sp[i], notes: e.target.value }; setF({ ...f, qc_specs: sp }); }} placeholder="Not" className="col-span-4 px-2 py-1 text-xs border rounded" data-testid={`fm-qc-notes-${i}`}/>
              <button onClick={() => setF({ ...f, qc_specs: (f.qc_specs || []).filter((_, j) => j !== i) })} className="col-span-1 text-red-500 text-xs">Sil</button>
            </div>
          ))}
          <Btn size="sm" variant="secondary" onClick={() => setF({ ...f, qc_specs: [...(f.qc_specs || []), { name: "", type: "number", notes: "" }] })}><Plus size={12}/> QC Parametresi</Btn>
        </div>

        <Textarea label="Genel Notlar" rows={3} value={f.notes || ""} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="fm-notes"/>
        </fieldset>
        <div className="flex justify-end gap-2 mt-4">
          {viewOnly ? (
            <>
              <Btn variant="secondary" onClick={() => { setOpen(false); setViewOnly(false); }} data-testid="fm-view-close">Kapat</Btn>
              {(isAdmin || (editing && editing.status !== "approved")) && (
                <Btn onClick={() => setViewOnly(false)} data-testid="fm-view-edit"><Pencil size={12}/> Düzenle</Btn>
              )}
              <Btn variant="secondary" onClick={() => downloadPdf(editing)} data-testid="fm-view-pdf"><FileText size={12}/> PDF İndir</Btn>
            </>
          ) : (
            <>
              <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
              <Btn onClick={save} data-testid="save-formula">{editing && editing.status === "approved" ? "Kaydet (Yeni Revizyon)" : "Kaydet"}</Btn>
            </>
          )}
        </div>
      </Modal>

      {/* ---- Envantere Kaydet Modal (Fix Pkg 05) ---- */}
      <Modal open={!!invDraft} onClose={() => setInvDraft(null)} title={`Envantere Kaydet — ${invDraft?.name || ""}`}>
        {invDraft && (
          <div className="space-y-3">
            <div className="text-[11px] bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 px-3 py-2 rounded">
              Formüldeki manuel malzeme envanter kaydına dönüştürülecek. İşlem tamamlandığında bu malzeme aktif tüm formüllerde ve satış siparişlerinde otomatik olarak bağlanacak.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Malzeme Adı *" value={invDraft.name} onChange={e => setInvDraft({ ...invDraft, name: e.target.value })} data-testid="fm-inv-name"/>
              <Select label="Kategori *" value={invDraft.category} onChange={e => setInvDraft({ ...invDraft, category: e.target.value })} data-testid="fm-inv-category">
                <option value="raw_material">Hammadde</option>
                <option value="active">Aktif Bileşen</option>
                <option value="fragrance">Parfüm/Koku</option>
                <option value="aux">Yardımcı Malzeme</option>
                <option value="primary_packaging">Birincil Ambalaj</option>
                <option value="secondary_packaging">İkincil Ambalaj</option>
                <option value="other_packaging">Diğer Ambalaj</option>
              </Select>
              <Select label="Birim" value={invDraft.unit} onChange={e => setInvDraft({ ...invDraft, unit: e.target.value })} data-testid="fm-inv-unit">
                <option value="kg">kg</option><option value="g">g</option><option value="l">l</option><option value="ml">ml</option><option value="adet">adet</option>
              </Select>
              <Input label="Birim Maliyet (EUR)" type="number" value={invDraft.unit_cost} onChange={e => setInvDraft({ ...invDraft, unit_cost: parseFloat(e.target.value) || 0 })} data-testid="fm-inv-unit-cost"/>
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setInvDraft(null)} disabled={invBusy}>Vazgeç</Btn>
              <Btn onClick={saveInvFromFormula} disabled={invBusy} data-testid="fm-inv-save">
                <Package size={12}/> {invBusy ? "Kaydediliyor..." : "Kaydet & Bağla"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ---- Toplu Envantere Kaydet Wizard (Iter 91) ---- */}
      <BulkInvWizard bulkInv={bulkInv} setBulkInv={setBulkInv} bulkBusy={bulkBusy} onSave={saveBulkInv} />

      {/* iter269 — Inline Yeni Müşteri Modalı: Formüller ekranından çıkmadan
          hızlıca müşteri kaydı oluşturmayı sağlar. Standart Müşteri Yönetimi
          formuyla aynı alanları içerir — normalize edilmiş legal_name mecburi. */}
      <Modal open={newCustOpen} onClose={() => !newCustBusy && setNewCustOpen(false)} title="Yeni Müşteri Kaydı" size="md">
        <div className="space-y-3">
          <Input label="Müşteri Adı *" value={newCust.legal_name} onChange={e => setNewCust({ ...newCust, legal_name: e.target.value })} data-testid="fm-newcust-name" placeholder="Örn: ACME Kozmetik San. Tic. A.Ş."/>
          <div className="grid grid-cols-3 gap-2">
            <Input label="Kısa Kod" value={newCust.code} onChange={e => setNewCust({ ...newCust, code: e.target.value })} data-testid="fm-newcust-code" placeholder="ACM01"/>
            <Input label="Şehir" value={newCust.city} onChange={e => setNewCust({ ...newCust, city: e.target.value })} data-testid="fm-newcust-city" placeholder="İstanbul"/>
            <Input label="Vergi No" value={newCust.tax_no} onChange={e => setNewCust({ ...newCust, tax_no: e.target.value })} data-testid="fm-newcust-taxno" placeholder="1234567890"/>
          </div>
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
            💡 Kaydettikten sonra Müşteri Yönetimi ekranından iletişim / marka / banka bilgilerini tamamlayabilirsiniz.
          </div>
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setNewCustOpen(false)} disabled={newCustBusy} data-testid="fm-newcust-cancel">Vazgeç</Btn>
            <Btn onClick={createCustomerInline} disabled={newCustBusy || !newCust.legal_name.trim()} data-testid="fm-newcust-save">
              {newCustBusy ? "Kaydediliyor..." : "Kaydet & Bağla"}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
